import { useState, useCallback, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface GifPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (gifUrl: string) => void;
}

interface GiphyGif {
  id: string;
  images: {
    fixed_width: { url: string; height: string };
    original: { url: string };
  };
}

export function GifPicker({ open, onOpenChange, onSelect }: GifPickerProps) {
  const [query, setQuery] = useState("");
  const [gifs, setGifs] = useState<GiphyGif[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchGifs = useCallback(async (searchQuery: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('gif-search', {
        body: { query: searchQuery },
      });
      if (error) throw error;
      setGifs(data?.data || []);
    } catch {
      setGifs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchGifs("");
    }
  }, [open, fetchGifs]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      fetchGifs(query);
    }
  };

  const handleSelect = (gif: GiphyGif) => {
    const url = gif.images?.fixed_width?.url || gif.images?.original?.url;
    if (url) {
      onSelect(url);
      onOpenChange(false);
      setQuery("");
      setGifs([]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Adicionar GIF</DialogTitle>
        </DialogHeader>
        <Input
          placeholder="Pesquisar GIFs..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
        <ScrollArea className="h-80">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : gifs.length > 0 ? (
            <div className="columns-3 gap-2 space-y-2">
              {gifs.map((gif) => (
                <button
                  key={gif.id}
                  type="button"
                  onClick={() => handleSelect(gif)}
                  className="block w-full rounded-lg overflow-hidden border border-border hover:border-primary transition-colors break-inside-avoid"
                >
                  <img
                    src={gif.images?.fixed_width?.url}
                    alt="GIF"
                    className="w-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8 text-sm">Nenhum GIF encontrado</p>
          )}
        </ScrollArea>
        <p className="text-[10px] text-muted-foreground text-center">Powered by GIPHY</p>
      </DialogContent>
    </Dialog>
  );
}
